import React, { useState, useRef } from 'react';
import cx from 'classnames';

import {
    getIconColor,
    getIconSize,
    getIconBtnVariantStyle,
    getIconBtnSizeStyle,
} from '@primitives/SplitButton/utils';
import { useOutsideClick } from '@hooks';

import { SplitButtonAction, SplitButtonProps } from '@primitives/SplitButton/types';

import ArrowDownIcon from '@icons/ArrowDownIcon';
import Typography from '@primitives/Typography';
import Button from '@primitives/Button';

import styles from './SplitButton.module.scss';

const SplitButton = React.forwardRef<HTMLButtonElement, SplitButtonProps>((props, ref) => {
    const {
        children,
        buttonClassName,
        OptionIcon = ArrowDownIcon,
        variant = 'primary',
        size = 'medium',
        disabled = false,
        actions = [],
        fullWidth = false,
        menuClassName,
        onClick,
        ...restButtonProps
    } = props;

    const [openMenu, setOpenMenu] = useState(false);
    const splitButtonGroupRef = useRef(null);

    const buttonStyles = cx(styles.splitButton, styles[`splitButton--${variant}`], buttonClassName);

    const optionButtonClass = cx(
        styles.splitButton__options,
        getIconBtnSizeStyle(size),
        getIconBtnVariantStyle(variant),
    );

    const menuClass = cx(
        styles.menu,
        {
            [styles['menu--fullWidth']]: fullWidth,
            [styles['menu--hidden']]: !openMenu,
        },
        menuClassName,
    );

    useOutsideClick(splitButtonGroupRef, () => setOpenMenu(false));

    const toggleMenu = (): void => {
        setOpenMenu((show) => !show);
    };

    const handleMenuItemClick = (option: SplitButtonAction): void => {
        const { onClick, isDisabled } = option;

        if (isDisabled) return;

        onClick();
        toggleMenu();
    };

    const handleMainButtonClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        if (onClick) onClick(e);
        else if (!openMenu) toggleMenu();
    };

    return (
        <div
            ref={splitButtonGroupRef}
            className={cx(styles.splitButton, {
                [styles['splitButton--fullWdith']]: fullWidth,
            })}
        >
            <Button
                ref={ref}
                className={buttonStyles}
                variant={variant}
                size={size}
                disabled={disabled}
                fullWidth={fullWidth}
                onClick={(e) => handleMainButtonClick(e)}
                data-testid="split-btn-main-cta"
                {...restButtonProps}
            >
                {children}
            </Button>

            <button
                className={optionButtonClass}
                disabled={disabled}
                onClick={toggleMenu}
                data-testid="split-btn-secondary-cta"
            >
                <OptionIcon color={getIconColor(variant)} size={getIconSize(size)} />
            </button>

            {openMenu && (
                <menu className={menuClass} data-testid="split-btn-menu">
                    {actions?.map((menuItem) => {
                        return (
                            <div
                                key={menuItem?.label}
                                className={cx(styles.menu__item, {
                                    [styles['menu--disabled']]: menuItem?.isDisabled,
                                })}
                                onClick={() => handleMenuItemClick(menuItem)}
                            >
                                <Typography variant="p3" className={styles['menu__item--text']}>
                                    {menuItem?.label}
                                </Typography>
                            </div>
                        );
                    })}
                </menu>
            )}
        </div>
    );
});

export default SplitButton;
